/**
 * Bot modes, in a Meteor-free module (like fleetResolve) so the bot process
 * and unit tests can import them without dragging in Mongo. bots.ts
 * re-exports everything here, so existing imports keep working.
 */

export type BotMode =
  | 'buy' | 'sell' | 'hybrid' | 'banker' | 'depot' | 'shredDepot' | 'shred' | 'census' | 'replenish' | 'deplete' | 'cash'
  | 'eagerBuy' | 'eagerSell' | 'eagerHybrid';

export const botModes: BotMode[] =
  ['buy', 'sell', 'hybrid', 'banker', 'depot', 'shredDepot', 'shred', 'census', 'replenish', 'deplete', 'cash',
    'eagerBuy', 'eagerSell', 'eagerHybrid'];

/** Modes that INITIATE trades with marketplace advertisers (cronEagerTrade)
 *  on top of their base behaviour. Keyed off orgmode, never off mode. */
export const eagerModes: BotMode[] = ['eagerBuy', 'eagerSell', 'eagerHybrid'];

/**
 * The behaviour a mode actually RUNS. Eager modes are skins: an eagerBuy bot
 * IS a buy bot -- same act(), same pricing, same deplete/classified gates --
 * plus one initiation cron keyed off orgmode. Identity for every other mode,
 * which is the isolation guarantee: a non-eager bot maps to itself at every
 * call site that uses this, so its behaviour is unchanged by construction.
 */
export const baseBehaviourMode = (mode: BotMode): BotMode =>
  mode === 'eagerBuy' ? 'buy'
    : mode === 'eagerSell' ? 'sell'
      : mode === 'eagerHybrid' ? 'hybrid'
        : mode;

/**
 * The canonical BotMode for a stored value, matched case-insensitively.
 * Null when the value is no mode at all.
 *
 * Exists because the Bot constructor used to `.toLowerCase()` the stored mode
 * outright, and 'shredDepot' is the one camelCase mode -- so a shredDepot bot
 * compared as 'shreddepot' against every SHREDDEPOTMODE check in the codebase
 * and matched none of them: not the coordinator pool, not its behaviour class,
 * not its housekeeping cron. Its own save() then wrote the mangled value back,
 * baking the breakage into the doc. Canonicalizing against the real list keeps
 * the case-insensitive tolerance without inventing values the code never
 * compares against.
 */
export const canonicalBotMode = (raw: unknown): BotMode | null => {
  const s = String(raw ?? '').trim().toLowerCase();
  return botModes.find((m) => m.toLowerCase() === s) ?? null;
};
